import React from 'react';
import { Container, Typography, Box, Grid, Card, CardContent, Chip, Link    } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SmartToy from '@mui/icons-material/SmartToy';
import Mic from '@mui/icons-material/Mic';
import Receipt from '@mui/icons-material/Receipt';
import FlashOn from '@mui/icons-material/FlashOn';
import CheckCircle from '@mui/icons-material/CheckCircle';
import LinkedIn from '@mui/icons-material/LinkedIn';
import Article from '@mui/icons-material/Article';
import ShowChart from '@mui/icons-material/ShowChart';
import SentimentSatisfiedAlt from '@mui/icons-material/SentimentSatisfiedAlt';
import PriceCheck from '@mui/icons-material/PriceCheck';
import TrendingUp from '@mui/icons-material/TrendingUp';
import { GoogleSheetsIcon, GoogleDriveIcon, OutputIcon } from '../infrastructure/customicons';
import InputIcon from '@mui/icons-material/Output';


const AutomationsPage = () => {
  const theme = useTheme();

  const styles = {
    page: {
      minHeight: '100vh',
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
      paddingTop: '0rem',
      paddingBottom: '5rem',
    },
    heroSection: {
      textAlign: 'center',
      py: 1,
      px: 1,
    },
    heroImage: {
      width: { xs: 128, md: 160 },
      height: { xs: 128, md: 160 },
    },
    h1: {
      fontSize: '2rem',
      margin: '1rem 0 0.5rem',
      fontWeight: 700,
    },
    h2: {
      mb: 2,
      textAlign: 'center',
    },
    subheading: {
      maxWidth: '48rem',
      mx: 'auto',
      lineHeight: 1.7,
    },
    solutionsSection: {
      py: 8,
      px: 3,
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f7fafc',
    },
    card: {
      borderRadius: '16px',
      p: 3,
      boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
      border: `1px solid ${theme.palette.divider}`,
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      mb: 1,
    },
    iconWrapper: {
      p: 1.5,
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[900],
      color: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
      borderRadius: '16px',
      mr: 2,
      display: 'flex',
    },
    feature: {
      display: 'flex',
      alignItems: 'flex-start',
      spaceX: 4,
      mb: 1,
    },
    featureIcon: {
      mt: 0.5,
      flexShrink: 0,
      mr: 2,
    },
  };

  const solutions = [
    {
      icon: <SmartToy sx={{ fontSize: 40 }} />,
      title: 'Smart Expense Tracker',
      description: 'Say goodbye to manual expense logging! Our comprehensive solution transforms how you manage finances through intelligent automation and seamless integrations.',
      features: [
        { icon: <GoogleDriveIcon />, title: 'Google Drive Integration', description: 'Everything in your google drive account, nothing in ours.' },
        { icon: <FlashOn />, title: 'Telegram Integration', description: 'Forward receipts via Telegram and watch them automatically sync to your Google Drive with proper categorization.' },
        { icon: <GoogleSheetsIcon />, title: 'Google Sheet Tracking', description: 'Every expense goes mothly expenses Google Sheet.' },
        { icon: <Mic />, title: 'Voice-Enabled Tracking', description: 'Simply speak your expenses and let AI handle the categorization and logging automatically.' },
        { icon: <Receipt />, title: 'Receipt Collection', description: 'Snap photos of receipts and our OCR technology extracts all relevant data automatically.' },
        { icon: <InputIcon />, title: 'How to input', description: 'Throw an audio, a photo or a text to our telegram bot.' },
        { icon: <OutputIcon />, title: 'Target', description: 'Every expense goes to a mothly google sheet.' },
        { icon: <CheckCircle />, title: 'Benefits', description: 'Gain clear insights into spending habits, reduce manual data entry by 95%, and maintain perfect expense records for tax season.' }, 
        { icon: <Article />, title: 'User Manual', description: <Link href="https://docs.google.com/document/d/1f4kN-vsj3aFHe501Hedi4jcfhD8W0t7tdnpLvRdZOP8" target="_blank" rel="noopener noreferrer" sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'primary.main' }}>See how the integration works</Link> },
      ],
    },
    {
      icon: <LinkedIn sx={{ fontSize: 40 }} />,
      title: 'LinkedIn Job Finder',
      description: 'Revolutionize your job search with AI-powered matching that scans thousands of opportunities and finds positions that align perfectly with your career goals.',
      features: [
        { icon: <GoogleDriveIcon />, title: 'Google Drive Integration', description: 'Everything in your google drive account, nothing in ours.' },
        { icon: <FlashOn />, title: 'Scheduler Trigger', description: 'CRON trigger that can be customized. By default runs Monday, Wednesday and Friday.' },
        { icon: <GoogleSheetsIcon />, title: 'Google Sheet Tracking', description: 'All new jobs go to a montly Google Sheet.' },
        { icon: <Article />, title: 'CV Analysis', description: 'Advanced AI analyzes your CV to understand your skills, experience, and career trajectory.' },
        { icon: <SmartToy />, title: 'Intelligent Matching', description: 'Our algorithm cross-references your profile with thousands of LinkedIn job postings in real-time and states why you might be the next talent for the company.' },
        { icon: <InputIcon />, title: 'How to Input', description: 'You add your name, CV and LinkedIn job search URLs to our input file' },
        { icon: <OutputIcon />, title: 'Target', description: 'Every job goes to a mothly google sheet categorized as matching or non-matching.' },
        { icon: <CheckCircle />, title: 'Benefits', description: 'Save hours per week on job searching while discovering more favoured opportunities you might have missed manually. You also get a monthly database and macro view of overall market.' },
        { icon: <Article />, title: 'User Manual', description: <Link href="https://docs.google.com/document/d/1CevtqtqAcq6CMhDGO19Ap2g5gfzxxqeL-J7KadnSQzo" target="_blank" rel="noopener noreferrer" sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'primary.main' }}>See how the integration works</Link> },

      ],
    },
    {
      icon: <ShowChart sx={{ fontSize: 40 }} />,
      title: 'Markets Assets Tracker',
      description: 'Track your favourite cryptos, stocks and forex portfolio by keeping daily sentiment knowledge and price data to favour your investments and trades.',
      tag: <Chip label="in development" color="primary" sx={{ ml: 2 }} />,
      features: [
        { icon: <GoogleDriveIcon />, title: 'Google Drive Integration', description: 'Everything in your google drive account, nothing in ours.' },
        { icon: <FlashOn />, title: 'Scheduler Trigger', description: 'CRON trigger that can be customized. By default runs every day.' },
        { icon: <GoogleSheetsIcon />, title: 'Google Sheet Tracking', description: 'All symbols you want to track go to a monthly Google sheet where the sheets in it are the symbols you are interested in tracking.' },
        { icon: <SentimentSatisfiedAlt />, title: 'Sentiment Analysis', description: 'Advanced AI analyzes the sentiment of your favourite assets.' },
        { icon: <PriceCheck />, title: 'Price monitoring', description: 'Tracks the price and volume of all assets.' },
        { icon: <InputIcon />, title: 'How to Input', description: 'Add your favourite crypto, stocks and forex to our input file' },
        { icon: <OutputIcon />, title: 'Output', description: 'Every symbol news sentiment and price goes to the specific sheet inside the monthly Google Sheet.' },
        { icon: <CheckCircle />, title: 'Benefits', description: 'Instead of research hours per week on your favourite symbols, let our automation do the work for you.' },
        { icon: <Article />, title: 'User Manual', description: <Link href="https://docs.google.com/document/d/1UNfOZHcWwYc3Lox-V91c_xMyarKjlc_KN4H53BIoDfc" target="_blank" rel="noopener noreferrer" sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'primary.main' }}>See how the integration works</Link> },

      ],
    },
  ];

  return (
    <Box sx={styles.page}>
      <Box sx={styles.heroSection}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0 }}>
            <Box
              component="img"
              src="https://imagizer.imageshack.com/v2/320x240q70/924/1yx2xL.png"
              alt="gdscreate Automations"
              sx={styles.heroImage}
            />
          </Box>
          <Typography variant="h5" color="text.primary" sx={styles.subheading}>
            Your partner in streamlining daily life. We develop intelligent solutions designed to
            automate your routine tasks and boost productivity through cutting-edge technology.
          </Typography>
        </Container>
      </Box>

      <Box sx={styles.solutionsSection}>
        <Container maxWidth="md">
          <Grid container spacing={5}>
            {solutions.map((solution, index) => (
              <Grid item xs={12} lg={6} key={index}>
                <Card sx={styles.card}>
                  <CardContent>
                    <Box sx={styles.cardHeader}>
                      <Box sx={styles.iconWrapper}>{solution.icon}</Box>
                      <Typography variant="h4" component="h3" fontWeight="600">
                        {solution.title}
                      </Typography>
                      {solution.tag}
                    </Box>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.7 }}>
                      {solution.description}
                    </Typography>
                    <Box>
                      {solution.features.map((feature, fIndex) => (
                        <Box sx={styles.feature} key={fIndex}>
                          <Box sx={styles.featureIcon} color="text.primary">{React.cloneElement(feature.icon, { sx: { fontSize: 24 } })}</Box>
                          <Box>
                            <Typography variant="h6" component="h6" fontWeight="100" sx={{ mb: 1, fontSize: '1.1rem' }}>
                              {feature.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {feature.description}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default AutomationsPage;
